/**
 * Question Bank API calls
 */

import apiClient from './client.js';
import {
  QuestionBank,
  QuestionBankWithQuestions,
  CreateQuestionBankInput,
  UpdateQuestionBankInput,
  PaginatedResponse,
} from '../../types/models';

export const questionBanksApi = {
  /**
   * List question banks
   */
  list: async (params?: {
    search?: string;
    created_by?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<QuestionBank>> => {
    const response = await apiClient.get('/question-banks', { params });
    return response.data;
  },

  /**
   * Get question bank by ID
   */
  getById: async (id: string): Promise<QuestionBank> => {
    const response = await apiClient.get(`/question-banks/${id}`);
    return response.data;
  },

  /**
   * Get question bank with questions
   */
  getByIdWithQuestions: async (id: string): Promise<QuestionBankWithQuestions> => {
    const response = await apiClient.get(`/question-banks/${id}/questions`);
    return response.data;
  },

  /**
   * Create question bank
   */
  create: async (data: CreateQuestionBankInput): Promise<QuestionBank> => {
    const response = await apiClient.post('/question-banks', data);
    return response.data;
  },

  /**
   * Update question bank
   */
  update: async (id: string, data: UpdateQuestionBankInput): Promise<QuestionBank> => {
    const response = await apiClient.patch(`/question-banks/${id}`, data);
    return response.data;
  },

  /**
   * Delete question bank
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/question-banks/${id}`);
  },

  /**
   * Link question bank to assessment
   */
  linkToAssessment: async (questionBankId: string, assessmentId: string, questionsToSelect?: number | null): Promise<void> => {
    const payload: any = {
      assessment_id: assessmentId,
    };
    // Only include questions_to_select if it's a valid number
    if (questionsToSelect !== null && questionsToSelect !== undefined && questionsToSelect > 0) {
      payload.questions_to_select = questionsToSelect;
    }
    await apiClient.post(`/question-banks/${questionBankId}/link`, payload);
  },

  /**
   * Unlink question bank from assessment
   */
  unlinkFromAssessment: async (questionBankId: string, assessmentId: string): Promise<void> => {
    await apiClient.post(`/question-banks/${questionBankId}/unlink`, { assessment_id: assessmentId });
  },

  /**
   * Get question banks for an assessment
   */
  getByAssessmentId: async (assessmentId: string): Promise<QuestionBank[]> => {
    const response = await apiClient.get(`/question-banks/assessment/${assessmentId}`);
    return response.data;
  },

  /**
   * Update questions to select for a linked question bank
   */
  updateQuestionsToSelect: async (questionBankId: string, assessmentId: string, questionsToSelect: number | null): Promise<void> => {
    await apiClient.patch(`/question-banks/${questionBankId}/update-questions-to-select`, {
      assessment_id: assessmentId,
      questions_to_select: questionsToSelect,
    });
  },

  /**
   * Analyze PDF and return preview of questions (does not create questions)
   */
  analyzePdf: async (id: string, file: File, context?: string): Promise<{
    questions: any[];
    total_questions: number;
    errors?: string[];
  }> => {
    const formData = new FormData();
    formData.append('pdf', file);
    if (context) {
      formData.append('context', context);
    }
    const response = await apiClient.post(`/question-banks/${id}/analyze-pdf`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Upload PDF and create questions (or create from provided questions array)
   */
  uploadPdf: async (
    id: string,
    file: File | null,
    questions?: any[],
    context?: string
  ): Promise<{
    message: string;
    questions_created: number;
    question_ids: string[];
    errors?: string[];
  }> => {
    const formData = new FormData();
    if (file) {
      formData.append('pdf', file);
    }
    if (context) {
      formData.append('context', context);
    }
    if (questions) {
      formData.append('questions', JSON.stringify(questions));
    }
    const response = await apiClient.post(`/question-banks/${id}/upload-pdf`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Analyze CSV files and return preview of questions (does not create questions)
   */
  analyzeCsv: async (
    id: string,
    questionsFile: File,
    optionsFile: File
  ): Promise<{
    questions: any[];
    total_questions: number;
    errors?: string[];
    warnings?: string[];
    parsing_method?: 'structured' | 'ai';
  }> => {
    const formData = new FormData();
    formData.append('questions_csv', questionsFile);
    formData.append('options_csv', optionsFile);
    const response = await apiClient.post(`/question-banks/${id}/analyze-csv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Analyze 3 SkillsUSA CSV files and return preview grouped by question bank
   */
  analyzeSkillsUSA: async (
    banksFile: File,
    questionsFile: File,
    optionsFile: File
  ): Promise<{
    banks: Array<{
      name: string;
      external_id: string;
      questions: any[];
      question_count: number;
    }>;
    total_banks: number;
    total_questions: number;
    errors: string[];
    warnings: string[];
  }> => {
    const formData = new FormData();
    formData.append('banks_csv', banksFile);
    formData.append('questions_csv', questionsFile);
    formData.append('options_csv', optionsFile);
    const response = await apiClient.post('/question-banks/skillsusa-analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Analyze a single denormalized SkillsUSA CSV file and return preview grouped by question bank
   */
  analyzeSkillsUSASingle: async (
    csvFile: File
  ): Promise<{
    banks: Array<{
      name: string;
      external_id: string;
      questions: any[];
      question_count: number;
    }>;
    total_banks: number;
    total_questions: number;
    errors: string[];
    warnings: string[];
  }> => {
    const formData = new FormData();
    formData.append('csv_file', csvFile);
    const response = await apiClient.post('/question-banks/skillsusa-analyze-single', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Import multiple question banks from SkillsUSA analysis
   */
  importSkillsUSA: async (
    banks: Array<{
      name: string;
      external_id: string;
      questions: any[];
    }>,
    createAssessment?: boolean,
    assessmentName?: string
  ): Promise<{
    banks_created: number;
    bank_ids: string[];
    total_questions_created: number;
    assessment_id?: string;
    errors?: string[];
  }> => {
    const response = await apiClient.post('/question-banks/skillsusa-import', {
      banks,
      create_assessment: createAssessment,
      assessment_name: assessmentName,
    });
    return response.data;
  },

  /**
   * Import CSV and create questions from provided questions array
   */
  importCsv: async (
    id: string,
    questions: any[]
  ): Promise<{
    message: string;
    questions_created: number;
    question_ids: string[];
    errors?: string[];
  }> => {
    const response = await apiClient.post(`/question-banks/${id}/import-csv`, {
      questions,
    });
    return response.data;
  },
};
